"use client"
import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box, CircularProgress, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Link from "next/link"
import React, { useCallback, useState } from "react"
import { colors, mediaQueries, sizes } from "ui"
import { downloadFile } from "utils/downloadFile"

export interface ReportCardProps {
  name?: string
  publicationDate?: {
    dateTime: string
  }
  url?: string
  sourceType?: string
  primaryColor?: string
  file?: {
    mediaItems?: Array<{
      url?: string
    }>
  }
}

const StyledCard = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 232px;
  width: 100%;
  padding: ${sizes.baseQuadric};
  border-radius: ${sizes.borderRadiusLarge};
  text-decoration: none;
  background-color: ${colors.white};
  box-shadow: 9.772px -9.772px 36.646px 0px rgba(15, 36, 79, 0.08);

  ${mediaQueries.desktopM} {
    width: 275px;
    padding: ${sizes.baseQuadric} ${sizes.baseSix};
  }
  ${mediaQueries.desktopXl} {
    width: 372px;
    padding: ${sizes.baseQuadric} ${sizes.baseSix};
  }
`

const BubbleWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bubbleColor",
})<{ bubbleColor?: string }>`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  gap: ${sizes.baseSingle};
  z-index: 2;
  margin: -${sizes.baseSingle} -${sizes.baseSingle} ${sizes.baseSingle} 0px;

  p {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    border-radius: ${sizes.borderRadiusLarge};
    background-color: ${({ bubbleColor }) =>
      bubbleColor ? `${bubbleColor}20` : `${colors.white}20`};
    border: ${({ bubbleColor }) =>
      bubbleColor ? `1px solid ${bubbleColor}` : `1px solid ${colors.black}`};
    color: ${colors.black};
    padding: 2px 12px;
    font-size: 16px;
  }
`

const StyledCardTitleWrapper = styled(Link)`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${colors.black};
  }
`

const StyledCardTitle = styled(Typography)`
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${colors.black};
  padding-bottom: ${sizes.baseTriple};
  height: 70px;
`

const StyledCardDate = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${sizes.baseSingle};
  margin-top: auto;
  padding-top: ${sizes.baseTriple};
  color: ${colors.red};
  font-size: 16px;
  font-weight: 500;
  border-top: 1px solid ${colors.borderGray};
`

const StyledIconsWrapper = styled(Typography)`
  display: flex;
  flex-direction: row;
  gap: ${sizes.baseDouble};
`

const StyledDownloadButton = styled.button<{ isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: ${({ isLoading }) => (isLoading ? "not-allowed" : "pointer")};
  padding: 0;
  transition: opacity 0.2s ease;
  position: relative;
  width: 24px;
  height: 24px;

  &:hover {
    opacity: ${({ isLoading }) => (isLoading ? 1 : 0.7)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .download-icon {
    opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
    transition: opacity 0.2s ease;
  }

  .loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    /* transform: translate(-50%, -50%); */
  }
`

const StyledPdfLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const ReportCard = ({
  data,
  className,
}: {
  data: ReportCardProps
  className?: string
}) => {
  const { name, publicationDate, sourceType, primaryColor, file } = data
  const t = useTranslations("ReportsCards")
  const [isDownloading, setIsDownloading] = useState(false)
  const fileUrl = file?.mediaItems?.[0]?.url
  const DownloadIcon = iconsMap["download"]
  const PdfIcon = iconsMap["pdf"]

  const date = publicationDate?.dateTime
    ? new Date(publicationDate.dateTime)
        .toLocaleDateString("he", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })
        .replace(/\//g, ".")
    : ""

  // Use useCallback to prevent recreation of this function on each render
  const handleDownload = useCallback(async () => {
    if (!fileUrl || isDownloading) return
    setIsDownloading(true)
    try {
      await downloadFile(fileUrl, name || "report")
    } catch (error) {
      console.error("Download failed:", error)
    } finally {
      setIsDownloading(false)
    }
  }, [fileUrl, isDownloading, name])

  return (
    <StyledCard className={className}>
      <BubbleWrapper bubbleColor={primaryColor}>
        <Typography variant="body1">{sourceType}</Typography>
      </BubbleWrapper>
      <StyledCardTitleWrapper href={fileUrl || "#"} prefetch={false}>
        <StyledCardTitle variant="h3">{name}</StyledCardTitle>
      </StyledCardTitleWrapper>
      <StyledCardDate>
        {`${t("publishedAt")} ${date}`}
        <StyledIconsWrapper>
          {fileUrl && (
            <StyledDownloadButton
              onClick={handleDownload}
              aria-label={`Download ${name || "report"}`}
              disabled={!fileUrl || isDownloading}
              isLoading={isDownloading}
            >
              <DownloadIcon className="download-icon" />
              {isDownloading && (
                <CircularProgress
                  size={20}
                  thickness={4}
                  className="loading-spinner"
                />
              )}
            </StyledDownloadButton>
          )}
          {fileUrl && (
            <StyledPdfLink
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              prefetch={false}
            >
              <PdfIcon />
            </StyledPdfLink>
          )}
        </StyledIconsWrapper>
      </StyledCardDate>
    </StyledCard>
  )
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ReportCard)
